/**
 * LinkContext — connects to the local NeuralDeck Bridge WebSocket and
 * exposes the live Ableton Link session state (BPM, phase, peer count).
 *
 * The bridge runs headless on the DJ's own laptop at ws://localhost:17573.
 * If the bridge isn't running we quietly retry every 5s — never surface an
 * error, since Link is an optional feature. Studio.jsx checks `connected`
 * before wiring the "Follow BPM" toggle.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const BRIDGE_URL = "ws://localhost:17573";
const RETRY_MS = 5000;

const LinkCtx = createContext(null);

export function LinkProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const [sessionBpm, setSessionBpm] = useState(null);
  const [beatPhase, setBeatPhase] = useState(0);
  const [peersCount, setPeersCount] = useState(0);
  const wsRef = useRef(null);
  const retryTimerRef = useRef(null);

  const connect = () => {
    try {
      const ws = new WebSocket(BRIDGE_URL);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        retryTimerRef.current = setTimeout(connect, RETRY_MS);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      ws.onmessage = (e) => {
        try {
          const m = JSON.parse(e.data);
          if (m.type === "link_tick") {
            setSessionBpm(m.session_bpm);
            setBeatPhase(m.beat_phase);
            setPeersCount(m.peers_count);
          }
        } catch { /* ignore */ }
      };
    } catch {
      retryTimerRef.current = setTimeout(connect, RETRY_MS);
    }
  };

  useEffect(() => {
    // Opt-in only: probing localhost triggers browser "local network access" prompts for every visitor.
    let enabled = false;
    try { enabled = localStorage.getItem("nd_link_bridge") === "1"; } catch { /* storage blocked */ }
    if (!enabled) return undefined;
    connect();
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBridgeBpm = (bpm) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "set_bpm", bpm }));
    }
  };

  return (
    <LinkCtx.Provider value={{ connected, sessionBpm, beatPhase, peersCount, setBridgeBpm }}>
      {children}
    </LinkCtx.Provider>
  );
}

export function useLink() {
  const ctx = useContext(LinkCtx);
  if (!ctx) return { connected: false, sessionBpm: null, beatPhase: 0, peersCount: 0, setBridgeBpm: () => {} };
  return ctx;
}
